import {
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  Grid,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
  TextField,
  Button,
  Autocomplete,
  IconButton,
  Tooltip,
  Paper,
  LinearProgress,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
} from "@mui/material";
import MenuView from "../../components/MenuView";
import LoadApi from "../../hooks/useApi";
import { useParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import SnackAlert from "../../components/SnackAlert";
import useSnack from "../../hooks/useSnack";
import BackButton from "../../components/BackButton";
import MinimalistCard from "../../components/MinimalistCard";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SendIcon from "@mui/icons-material/Send";

import StyledTable from "./components/StyledTable";
import OrderStatus from "./components/OrderStatus";

export default function View() {
  const { id } = useParams();
  const { loadApi } = LoadApi();
  const [data, setData] = useState([]);
  const [status, setStatus] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [isPriority, setIsPriority] = useState(false);
  const { showSnack, msgSB, openSB, typeSB } = useSnack();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const getData = () => {
    setLoading(true);
    loadApi(`admin/orders/${id}`, true, "get")
      .then((response) => {
        setData(response.data);
        setIsPriority(response.data?.priority || false);
      })
      .catch((error) => {
        showSnack(error.message, "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getStatus = () => {
    loadApi(`admin/orders/status/v2`, true, "get")
      .then((response) => {
        setStatus(response.data);
        // Establecer el estado actual del pedido como valor seleccionado
        if (data?.status) {
          setSelectedStatus(data.status);
        }
      })
      .catch((error) => {
        showSnack(error.message, "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTogglePriority = async () => {
    // Verificar si el estado es "Procesando" antes de permitir cambios de prioridad
    if (data?.status !== "Procesando") {
      showSnack(
        "Solo se puede marcar como prioritario los pedidos en estado 'Procesando'",
        "warning"
      );
      return;
    }

    setUpdating(true);
    try {
      await loadApi(`admin/orders/${id}`, true, "put");
      setIsPriority(!isPriority);
      showSnack(
        `Pedido ${!isPriority ? "marcado como" : "desmarcado de"} prioridad`,
        "success"
      );
    } catch (error) {
      showSnack("Error al actualizar la prioridad", "error");
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    getData();
    getStatus();
  }, []);

  useEffect(() => {
    // Actualizar el estado seleccionado cuando se carguen los datos del pedido
    if (data?.status) {
      setSelectedStatus(data.status);
    }
  }, [data]);

  // Obtener los documentos del pedido
  const getDocuments = () => {
    if (data?.OrderInvoice?.document && data.OrderInvoice.document.length > 0) {
      // Solo mostrar el primer documento (índice 0)
      const firstDocument = data.OrderInvoice.document[0];
      setDocuments([
        {
          id: 0, // Índice 0
          fileName: firstDocument.name,
          url: firstDocument.url,
          uploadDate: data.OrderInvoice.createdAt,
        },
      ]);
    } else {
      setDocuments([]);
    }
  };

  // Actualizar los documentos cuando cambia el pedido
  useEffect(() => {
    if (data?.OrderInvoice) {
      getDocuments();
    }
  }, [data?.OrderInvoice]);

  // Función para determinar el icono según el tipo de archivo
  const getFileIcon = (fileName) => {
    if (!fileName) return <InsertDriveFileIcon />;

    const extension = fileName.split(".").pop().toLowerCase();

    if (["pdf"].includes(extension)) {
      return <PictureAsPdfIcon color="error" />;
    } else if (["jpg", "jpeg", "png", "gif", "bmp"].includes(extension)) {
      return <ImageIcon color="primary" />;
    } else if (["doc", "docx"].includes(extension)) {
      return <InsertDriveFileIcon color="primary" />;
    } else if (["xls", "xlsx"].includes(extension)) {
      return <InsertDriveFileIcon color="success" />;
    }

    return <InsertDriveFileIcon />;
  };

  // Abrir menú de opciones
  const handleMenuOpen = (event, documentId, index) => {
    setAnchorEl(event.currentTarget);
    setSelectedDocumentId(documentId);
    setSelectedDocumentIndex(index);
  };

  // Cerrar menú de opciones
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Descargar documento
  const handleDownload = () => {
    const document = documents.find((doc) => doc.id === selectedDocumentId);
    if (!document) return;

    // Usar la URL directa del documento
    window.open(document.url, "_blank");

    handleMenuClose();
  };

  // Editar documento (abre el selector de archivos)
  const handleEdit = () => {
    setEditMode(true);
    fileInputRef.current.click();
    handleMenuClose();
  };

  // Abrir diálogo de confirmación para eliminar
  const handleDeleteConfirm = () => {
    setConfirmDeleteOpen(true);
    handleMenuClose();
  };

  // Función para crear un nuevo documento
  const createDocument = async (file) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress((prevProgress) => {
          const newProgress = prevProgress + Math.random() * 10;
          return newProgress >= 90 ? 90 : newProgress;
        });
      }, 300);

      await loadApi(
        `admin/invoice/${data.order_number}`,
        true,
        "post",
        { document: file },
        true
      );

      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadSuccess(true);

      showSnack("Documento subido correctamente", "success");
      getData();

      setTimeout(() => {
        setUploadProgress(0);
      }, 2000);
    } catch (error) {
      showSnack(`Error al subir el documento: ${error.message}`, "error");
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  // Función para actualizar un documento existente (PUT)
  const updateDocument = async (file) => {
    setUploading(true);
    setUploadProgress(0);
    try {
      const progressInterval = setInterval(() => {
        setUploadProgress((prevProgress) => {
          const newProgress = prevProgress + Math.random() * 10;
          return newProgress >= 90 ? 90 : newProgress;
        });
      }, 300);

      await loadApi(
        `admin/invoice/${data.OrderInvoice.id}`,
        true,
        "put",
        { document_edit: file },
        true
      );

      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadSuccess(true);

      showSnack("Documento actualizado correctamente", "success");
      getData();

      setTimeout(() => {
        setUploadProgress(0);
      }, 2000);
    } catch (error) {
      showSnack(`Error al actualizar el documento: ${error.message}`, "error");
      setUploadProgress(0);
    } finally {
      setUploading(false);
      setEditMode(false);
    }
  };

  const deleteDocument = async () => {
    try {
      await loadApi(`admin/invoice/${data.OrderInvoice.id}`, true, "delete");

      // Actualizar los estados relevantes para volver al estado inicial
      setData((prevData) => ({
        ...prevData,
        OrderInvoice: null,
      }));
      setDocuments([]);
      setSelectedFile(null);
      setUploadProgress(0);
      setUploadSuccess(false);
      setEditMode(false);

      // Limpiar el input de archivo para permitir seleccionar el mismo archivo nuevamente
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      showSnack("Documento eliminado correctamente", "success");
    } catch (error) {
      showSnack(`Error al eliminar el documento: ${error.message}`, "error");
    } finally {
      setConfirmDeleteOpen(false);
    }
  };

  // Cerrar diálogo de confirmación
  const handleCloseConfirmDelete = () => {
    setConfirmDeleteOpen(false);
  };

  // Esta función decide qué operación ejecutar según el modo
  const uploadFile = async (file) => {
    if (editMode) {
      await updateDocument(file);
    } else {
      await createDocument(file);
    }
  };

  // Función para manejar la selección de archivos
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadSuccess(false);
      uploadFile(file);
    }
  };

  // Función para manejar la eliminación de documentos
  const handleDelete = () => {
    deleteDocument();
  };

  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Limpiar el valor antes de abrir el selector de archivos
    }
    fileInputRef.current.click();
  };

  const handleSentEmail = () => {
    setLoading(true);
    loadApi(`admin/invoice/${id}/send`, true, "post")
      .then((response) => {
        showSnack(`${response.data.message}`, "success");
      })
      .catch((error) => {
        showSnack(error.message, "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getFileNameToShow = () => {
    if (!selectedFile) return "";
    return selectedFile.name.length > 20
      ? selectedFile.name.substring(0, 17) + "..."
      : selectedFile.name;
  };

  // Verificamos si ya existe un documento asociado al pedido
  const hasDocument = documents.length > 0;

  return (
    <MenuView>
      <Box
        sx={{
          p: { xs: 0.5, sm: 2 },
          height: { xs: "auto", sm: "89vh" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Encabezado */}
        <Stack
          direction={isSmall ? "column" : "row"}
          spacing={isSmall ? 1 : 2}
          justifyContent="space-between"
          sx={{ mb: 1, px: { xs: 1, sm: 2 } }}
        >
          <Stack direction="row" spacing={2}>
            <BackButton />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant={isSmall ? "h5" : "h4"}>
              Pedido #{data?.order_number}
            </Typography>
            <Tooltip
              title={
                data?.status !== "Procesando"
                  ? "Solo los pedidos en estado 'Procesando' pueden ser prioritarios"
                  : isPriority
                  ? "Quitar prioridad"
                  : "Marcar como prioritario"
              }
            >
              <span>
                <IconButton
                  color="inherit"
                  onClick={handleTogglePriority}
                  disabled={
                    updating || loading || data?.status !== "Procesando"
                  }
                >
                  {isPriority ? (
                    <StarIcon
                      sx={{
                        color: "#f9de4b",
                        fontSize: isSmall ? "1.5rem" : "2rem",
                      }}
                    />
                  ) : (
                    <StarBorderIcon
                      sx={{ fontSize: isSmall ? "1.5rem" : "2rem" }}
                    />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Box
          sx={{
            flex: 1,
            pb: 1,
            maxHeight: { xs: "auto", sm: "calc(100vh - 1px)" },
            px: { xs: 1, sm: 0 },
          }}
        >
          <Grid
            container
            spacing={{ xs: 1, sm: 2, md: 3 }}
            sx={{ mt: 2, height: "100%" }}
          >
            <Grid item xs={12} md={8}>
              <Grid
                container
                spacing={{ xs: 1, sm: 2, md: 3 }}
                sx={{ overflowY: "auto" }}
              >
                <Grid item xs={12}>
                  <StyledTable data={data} isSmall={isSmall} />
                </Grid>

                <Grid item xs={12}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 1,
                      border: isPriority ? "2px solid #f9de4b" : undefined,
                      borderRadius: 2,
                      mb: 2,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 2,
                            fontSize: { xs: "1rem", sm: "1.25rem" },
                          }}
                        >
                          Factura
                        </Typography>
                        <ReceiptIcon />
                      </Stack>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={!hasDocument}
                        sx={{
                          backgroundColor: "#f9de4b",
                          padding: "5px 10px",
                          color: "black",
                          borderRadius: "8px",
                        }}
                        onClick={handleSentEmail}
                      >
                        Mandar factura
                        <SendIcon sx={{ ml: 1 }} />
                      </Button>
                    </Stack>
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    />

                    {documents.length > 0 ? (
                      <List dense sx={{ mb: 2 }}>
                        {documents.map((doc, index) => (
                          <ListItem
                            key={doc.id || index}
                            disablePadding
                            secondaryAction={
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={(e) =>
                                  handleMenuOpen(e, doc.id || index, index)
                                }
                              >
                                <MoreVertIcon />
                              </IconButton>
                            }
                            sx={{
                              border: "1px solid rgba(0, 0, 0, 0.12)",
                              borderRadius: "4px",
                              mb: 1,
                              pl: 1,
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {getFileIcon(doc.fileName || doc.name)}
                            </ListItemIcon>
                            <ListItemText
                              primary={doc.fileName || doc.name || "Documento"}
                              secondary={
                                doc.uploadDate &&
                                new Date(doc.uploadDate).toLocaleDateString()
                              }
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: { xs: "150px", sm: "230px" },
                                },
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, textAlign: "center" }}
                      >
                        No hay documentos adjuntos
                      </Typography>
                    )}

                    {/* Mostrar el botón de subir solo si no hay documentos */}
                    {!hasDocument ? (
                      <Box>
                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={
                            uploadSuccess ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <UploadFileIcon />
                            )
                          }
                          onClick={handleFileButtonClick}
                          disabled={uploading}
                          sx={{
                            height: 48,
                            borderRadius: 2,
                            textTransform: "none",
                            borderWidth: "2px",
                            borderStyle: "dashed",
                            borderColor: uploadSuccess
                              ? "success.main"
                              : "primary.main",
                            "&:hover": {
                              borderWidth: "2px",
                              borderStyle: "dashed",
                            },
                            backgroundColor: uploadSuccess
                              ? "rgba(76, 175, 80, 0.08)"
                              : "transparent",
                          }}
                        >
                          {uploading
                            ? "Subiendo..."
                            : uploadSuccess
                            ? "¡Archivo subido!"
                            : "Subir nuevo documento"}
                        </Button>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: "center" }}
                          >
                            Ya existe un documento asociado a este pedido.
                            Utiliza el botón de edición para reemplazarlo si es
                            necesario.
                          </Typography>
                        </Box>
                      </Alert>
                    )}

                    {selectedFile && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 1 }}
                      >
                        <AttachFileIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {getFileNameToShow()}
                        </Typography>
                      </Box>
                    )}

                    {uploadProgress > 0 && (
                      <Box sx={{ width: "100%", mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={uploadProgress}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: "rgba(0,0,0,0.1)",
                            "& .MuiLinearProgress-bar": {
                              backgroundColor: uploadSuccess
                                ? "success.main"
                                : "primary.main",
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                <Grid item xs={12} sm={12}>
                  <OrderStatus
                    data={data}
                    isSmall={isSmall}
                    isPriority={isPriority}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MinimalistCard
                    sx={{
                      p: { xs: 1, sm: 2 },
                      height: { xs: "auto", sm: "250px" },
                      overflow: "auto",
                      border: isPriority ? "2px solid #f9de4b" : undefined,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1,
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                      }}
                    >
                      Información de Facturación
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    {data?.BillingInfo?.infoBilling
                      ?.split("\n")
                      .map((line, index) => (
                        <Typography
                          key={index}
                          variant={isSmall ? "body2" : "body1"}
                        >
                          {line}
                        </Typography>
                      ))}

                    <Typography variant={isSmall ? "body2" : "body1"}>
                      <strong>Email:</strong> {data?.BillingInfo?.email}
                    </Typography>
                    <Typography variant={isSmall ? "body2" : "body1"}>
                      <strong>Teléfono:</strong> {data?.BillingInfo?.phone}
                    </Typography>
                  </MinimalistCard>
                </Grid>

                <Grid item xs={12}>
                  <MinimalistCard
                    sx={{
                      p: 1,
                      height: { xs: "auto", sm: "240px" },
                      overflow: "auto",
                      border: isPriority ? "2px solid #f9de4b" : undefined,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1,
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                      }}
                    >
                      Información de Envío
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    {data?.ShippingInfo?.infoShipping
                      ?.split("\n")
                      .map((line, index) => (
                        <Typography
                          key={index}
                          variant={isSmall ? "body2" : "body1"}
                        >
                          {line}
                        </Typography>
                      ))}

                    <Typography variant={isSmall ? "body2" : "body1"}>
                      <strong>Email:</strong> {data?.ShippingInfo?.email}
                    </Typography>
                    <Typography variant={isSmall ? "body2" : "body1"}>
                      <strong>Teléfono:</strong> {data?.ShippingInfo?.phone}
                    </Typography>
                  </MinimalistCard>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Menú de opciones para documentos */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDownload} dense>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Descargar" />
        </MenuItem>
        <MenuItem onClick={handleEdit} dense>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Reemplazar" />
        </MenuItem>
        <MenuItem
          onClick={handleDeleteConfirm}
          dense
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="Eliminar" />
        </MenuItem>
      </Menu>

      {/* Diálogo de confirmación para eliminar */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={handleCloseConfirmDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"¿Eliminar documento?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Esta acción no se puede deshacer. ¿Estás seguro de que deseas
            eliminar este documento?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pr: 2, gap: 1 }}>
          <Button
            onClick={handleCloseConfirmDelete}
            color="primary"
            sx={{ borderRadius: 4, color: "black" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            autoFocus
            variant="outlined"
            sx={{ borderRadius: 4 }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || updating}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </MenuView>
  );
}
